// src/pages/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, onSnapshot, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import { Line } from 'react-chartjs-2';
import LoadingSpinner from '../components/LoadingSpinner';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const Dashboard = () => {
  const [stats, setStats] = useState({
    total: 0,
    sliders: 0,
    news: 0,
    notifications: 0,
    gallery: 0,
    faculty: 0,
    events: 0,
    courses: 0,
    testimonials: 0,
    departments: 0
  });
  const [recentNews, setRecentNews] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [recentTestimonials, setRecentTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState('');

  const targetCollections = ['sliders', 'news', 'notifications', 'gallery', 'faculty', 'events', 'courses', 'testimonials', 'departments'];

  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }));
    };
    updateClock();
    const clockInterval = setInterval(updateClock, 1000);

    loadStats();
    loadRecentItems();
    setupRealtimeListeners();

    return () => {
      clearInterval(clockInterval);
    };
  }, []);

  const loadStats = async () => {
    try {
      const counterStore = {};
      for (const coll of targetCollections) {
        const snap = await getDocs(collection(db, coll));
        counterStore[coll] = snap.size;
      }
      const combinedTotal = Object.values(counterStore).reduce((acc, current) => acc + current, 0);
      setStats({
        total: combinedTotal,
        sliders: counterStore.sliders || 0,
        news: counterStore.news || 0,
        notifications: counterStore.notifications || 0,
        gallery: counterStore.gallery || 0,
        faculty: counterStore.faculty || 0,
        events: counterStore.events || 0,
        courses: counterStore.courses || 0,
        testimonials: counterStore.testimonials || 0,
        departments: counterStore.departments || 0
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadRecentItems = async () => {
    try {
      // Recent News
      const newsSnap = await getDocs(query(collection(db, 'news'), orderBy('createdAt', 'desc'), limit(4)));
      const newsData = [];
      newsSnap.forEach(doc => newsData.push({ id: doc.id, ...doc.data() }));
      setRecentNews(newsData);

      // Recent Events
      const eventsSnap = await getDocs(query(collection(db, 'events'), orderBy('createdAt', 'desc'), limit(4)));
      const eventsData = [];
      eventsSnap.forEach(doc => eventsData.push({ id: doc.id, ...doc.data() }));
      setRecentEvents(eventsData);

      // Recent Testimonials
      const testSnap = await getDocs(query(collection(db, 'testimonials'), orderBy('createdAt', 'desc'), limit(4)));
      const testData = [];
      testSnap.forEach(doc => testData.push({ id: doc.id, ...doc.data() }));
      setRecentTestimonials(testData);

      setLoading(false);
    } catch (error) {
      console.error('Error loading recent items:', error);
      setLoading(false);
    }
  };

  const setupRealtimeListeners = () => {
    targetCollections.forEach((coll) => {
      onSnapshot(collection(db, coll), () => {
        loadStats();
      });
    });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'No date';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const days = Math.floor(Math.abs(new Date() - date) / (1000 * 60 * 60 * 24));
      if (days === 0) return 'Today';
      if (days === 1) return 'Yesterday';
      if (days < 7) return `${days} days ago`;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return 'Invalid date';
    }
  };

  const chartData = {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [{
      label: 'Platform Activity',
      data: [25, 38, 30, 52, 41, 44, 58],
      borderColor: '#0ea5e9',
      backgroundColor: 'rgba(14, 165, 233, 0.08)',
      borderWidth: 3,
      pointBackgroundColor: '#0ea5e9',
      pointBorderColor: '#ffffff',
      pointRadius: 5,
      tension: 0.25,
      fill: true
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: '#334155',
          font: { weight: 'bold' }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#334155' }
      },
      y: {
        grid: { color: 'rgba(14, 165, 233, 0.15)' },
        ticks: { color: '#334155' }
      }
    }
  };

  const styles = `
    .dashboard-page {
      animation: structuralSlideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
    }

    .dashboard-split-view {
      display: grid;
      grid-template-columns: 2fr 1fr;
      gap: 32px;
      margin-bottom: 32px;
    }

    .dashboard-twin-view {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 32px;
      margin-bottom: 32px;
    }

    .card-panel {
      background: var(--glass-bg);
      backdrop-filter: var(--glass-blur);
      border-radius: var(--radius-rigid);
      padding: 32px;
      border: var(--glass-border);
      box-shadow: var(--muscular-shadow);
      transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    }

    .card-panel:hover {
      box-shadow: var(--lift-shadow);
      border-color: rgba(14, 165, 233, 0.3);
    }

    .panel-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      border-bottom: 1px solid rgba(14, 165, 233, 0.15);
      padding-bottom: 14px;
    }

    .panel-header h3 {
      font-family: 'Space Grotesk', sans-serif;
      font-size: 1.1rem;
      font-weight: 700;
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .text-link-btn {
      color: var(--clr-blue);
      text-decoration: none;
      font-size: 0.85rem;
      font-weight: 800;
    }

    .text-link-btn:hover {
      text-decoration: underline;
    }

    .chart-wrapper {
      position: relative;
      height: 320px;
      width: 100%;
    }

    .data-list {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .data-row {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 14px 16px;
      background: rgba(255, 255, 255, 0.3);
      border-radius: 10px;
      border: 1px solid rgba(255, 255, 255, 0.4);
      transition: all 0.2s;
    }

    .data-row:hover {
      transform: translateX(6px);
      background: rgba(255, 255, 255, 0.6);
    }

    .data-row-icon {
      width: 42px;
      height: 42px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.6);
    }

    .data-row-body {
      flex: 1;
    }

    .data-row-title {
      font-weight: 700;
      font-size: 0.9rem;
    }

    .data-row-meta {
      font-size: 0.75rem;
      color: var(--text-secondary);
      margin-top: 4px;
    }

    .badge-pill {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 4px;
      background: var(--clr-blue-glow);
      color: var(--clr-blue);
      font-weight: 700;
      font-size: 9px;
    }

    .action-matrix-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 16px;
    }

    .quick-action-tile {
      background: rgba(255, 255, 255, 0.3);
      border: 1px solid rgba(255, 255, 255, 0.4);
      padding: 20px;
      border-radius: 10px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
      cursor: pointer;
      transition: all 0.25s;
      text-decoration: none;
      color: var(--text-premium);
    }

    .quick-action-tile:hover {
      transform: translateY(-4px);
      background: rgba(255, 255, 255, 0.6);
    }

    .quick-action-tile i {
      font-size: 20px;
    }

    .act-blue { border-left: 4px solid var(--clr-blue); }
    .act-blue i { color: var(--clr-blue); }
    .act-emerald { border-left: 4px solid var(--clr-emerald); }
    .act-emerald i { color: var(--clr-emerald); }
    .act-amber { border-left: 4px solid var(--clr-amber); }
    .act-amber i { color: var(--clr-amber); }
    .act-purple { border-left: 4px solid var(--clr-purple); }
    .act-purple i { color: var(--clr-purple); }

    .state-blank {
      text-align: center;
      padding: 40px;
      font-weight: 600;
      color: var(--text-secondary);
    }

    @media (max-width: 1024px) {
      .dashboard-split-view {
        grid-template-columns: 1fr;
      }
      .dashboard-twin-view {
        grid-template-columns: 1fr;
      }
    }

    @media (max-width: 768px) {
      .action-matrix-grid {
        grid-template-columns: 1fr;
      }
      .stats-matrix {
        grid-template-columns: repeat(2, 1fr);
      }
    }
  `;

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <style>{styles}</style>
      <div className="dashboard-page">
        <div className="welcome-hero">
          <h1>Welcome back, Admin</h1>
          <p>Operational databases and dynamic communication metrics are healthy.</p>
          <div className="hero-meta-date">
            <i className="far fa-clock"></i> {currentTime}
          </div>
        </div>

        <div className="stats-matrix">
          <div className="matrix-card card-blue">
            <div>
              <div className="matrix-value">{stats.total}</div>
              <div className="matrix-label">Total Assets</div>
            </div>
            <div className="matrix-icon-box"><i className="fas fa-database"></i></div>
          </div>
          <div className="matrix-card card-emerald">
            <div>
              <div className="matrix-value">{stats.news}</div>
              <div className="matrix-label">News Articles</div>
            </div>
            <div className="matrix-icon-box"><i className="fas fa-newspaper"></i></div>
          </div>
          <div className="matrix-card card-amber">
            <div>
              <div className="matrix-value">{stats.events}</div>
              <div className="matrix-label">Live Events</div>
            </div>
            <div className="matrix-icon-box"><i className="fas fa-calendar-days"></i></div>
          </div>
          <div className="matrix-card card-purple">
            <div>
              <div className="matrix-value">{stats.faculty}</div>
              <div className="matrix-label">Faculty Staff</div>
            </div>
            <div className="matrix-icon-box"><i className="fas fa-user-tie"></i></div>
          </div>
        </div>

        <div className="dashboard-split-view">
          <div className="card-panel">
            <div className="panel-header">
              <h3><i className="fas fa-chart-bar"></i> Dynamic Platform Metrics</h3>
            </div>
            <div className="chart-wrapper">
              <Line data={chartData} options={chartOptions} />
            </div>
          </div>
          <div className="card-panel">
            <div className="panel-header">
              <h3><i className="fas fa-bolt-lightning"></i> Quick Actions</h3>
            </div>
            <div className="action-matrix-grid">
              <Link to="/news" className="quick-action-tile act-blue">
                <i className="fas fa-file-circle-plus"></i>
                <span>Publish News</span>
              </Link>
              <Link to="/events" className="quick-action-tile act-emerald">
                <i className="fas fa-calendar-plus"></i>
                <span>Schedule Event</span>
              </Link>
              <Link to="/faculty" className="quick-action-tile act-amber">
                <i className="fas fa-user-plus"></i>
                <span>Add Faculty</span>
              </Link>
              <Link to="/testimonials" className="quick-action-tile act-purple">
                <i className="fas fa-quote-left"></i>
                <span>Log Review</span>
              </Link>
            </div>
          </div>
        </div>

        <div className="dashboard-twin-view">
          <div className="card-panel">
            <div className="panel-header">
              <h3><i className="fas fa-newspaper"></i> Press & Announcements</h3>
              <Link to="/news" className="text-link-btn">View Feed →</Link>
            </div>
            <div className="data-list">
              {recentNews.length === 0 ? (
                <div className="state-blank">No news articles</div>
              ) : (
                recentNews.map((item) => (
                  <div key={item.id} className="data-row" style={{ borderLeft: '4px solid #0ea5e9' }}>
                    <div className="data-row-icon" style={{ color: '#0ea5e9' }}>
                      <i className="fas fa-newspaper"></i>
                    </div>
                    <div className="data-row-body">
                      <div className="data-row-title">{item.title || 'Untitled'}</div>
                      <div className="data-row-meta">
                        {formatDate(item.createdAt)} • <span className="badge-pill">{item.category || 'General'}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          <div className="card-panel">
            <div className="panel-header">
              <h3><i className="fas fa-calendar-day"></i> Scheduled Events</h3>
              <Link to="/events" className="text-link-btn">View All →</Link>
            </div>
            <div className="data-list">
              {recentEvents.length === 0 ? (
                <div className="state-blank">No events scheduled</div>
              ) : (
                recentEvents.map((item) => (
                  <div key={item.id} className="data-row" style={{ borderLeft: '4px solid #10b981' }}>
                    <div className="data-row-icon" style={{ color: '#10b981' }}>
                      <i className="fas fa-calendar-day"></i>
                    </div>
                    <div className="data-row-body">
                      <div className="data-row-title">{item.title || 'Untitled'}</div>
                      <div className="data-row-meta">
                        {formatDate(item.eventDate || item.createdAt)} • <strong>{item.venue || 'Main Campus'}</strong>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="dashboard-twin-view">
          <div className="card-panel">
            <div className="panel-header">
              <h3><i className="fas fa-star"></i> Incoming Reviews</h3>
              <Link to="/testimonials" className="text-link-btn">Review Center →</Link>
            </div>
            <div className="data-list">
              {recentTestimonials.length === 0 ? (
                <div className="state-blank">No reviews yet</div>
              ) : (
                recentTestimonials.map((item) => {
                  const rating = parseInt(item.rating) || 5;
                  return (
                    <div key={item.id} className="data-row" style={{ borderLeft: '4px solid #8b5cf6' }}>
                      <div className="data-row-icon" style={{ color: '#8b5cf6' }}>
                        <i className="fas fa-star"></i>
                      </div>
                      <div className="data-row-body">
                        <div className="data-row-title">{item.studentName || 'Anonymous'}</div>
                        <div className="data-row-meta" style={{ color: '#ea580c' }}>
                          {'★'.repeat(rating)}{'☆'.repeat(5 - rating)}
                          <span style={{ color: 'var(--text-secondary)' }}> • {item.course || 'Program'}</span>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          <div className="card-panel">
            <div className="panel-header">
              <h3><i className="fas fa-building"></i> Departments Overview</h3>
              <Link to="/departments" className="text-link-btn">Manage →</Link>
            </div>
            <div className="data-list">
              <div className="state-blank">
                <i className="fas fa-building" style={{ fontSize: '32px', display: 'block', marginBottom: '12px', color: 'var(--text-dimmed)' }}></i>
                <span>Total Departments: <strong>{stats.departments}</strong></span>
                <br />
                <span style={{ fontSize: '0.8rem' }}>Click "Manage" to view and edit department content</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;